import React, { useState } from 'react';
import { formatValue } from '../../utils/formatters';
import SummaryCard from '../shared/SummaryCard';
import ContactDetailModal from '../ContactDetailModal';

const ContactsSection = ({ contacts, onViewContacts }) => {
    const [selectedContact, setSelectedContact] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    if (!contacts || typeof contacts !== 'object') {
        return null;
    }

    const extractionStats = contacts.extraction_stats || {};
    const contactsList = contacts.contacts || [];
    const contactsCount = contactsList.length;

    // Get contact display name
    const getContactName = (contact) => {
        if (contact.firstname || contact.lastname) {
            return `${contact.firstname || ''} ${contact.lastname || ''}`.trim() || 'Unnamed Contact';
        }
        if (contact.role) {
            return contact.role;
        }
        if (contact.email) {
            return contact.email;
        }
        if (contact.phone) {
            return contact.phone;
        }
        return 'Unnamed Contact';
    };

    // Get contact summary info
    const getContactSummary = (contact) => {
        const parts = [];
        if (contact.email) parts.push(`📧 ${contact.email}`);
        if (contact.phone) parts.push(`📞 ${contact.phone}`);
        if (contact.role) parts.push(`💼 ${contact.role}`);
        return parts.length > 0 ? parts.join(' • ') : 'No contact info';
    };

    // Get score color
    const getScoreColor = (score) => {
        if (score >= 7) return '#34d399'; // green
        if (score >= 5) return '#fbbf24'; // yellow
        if (score >= 3) return '#f97316'; // orange
        return '#f87171'; // red
    };

    const handleContactClick = (contact) => {
        setSelectedContact(contact);
        setIsDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedContact(null);
    };

    return (
        <>
            <div style={{ marginBottom: '30px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                }}>
                    <h4 style={{ color: '#e0e0e0', fontWeight: '600', margin: 0 }}>Contacts</h4>
                    {contactsCount > 0 && onViewContacts && (
                        <button
                            onClick={onViewContacts}
                            style={{
                                backgroundColor: '#3a3a3a',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 14px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#4a4a4a'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                        >
                            View all in modal ({contactsCount})
                        </button>
                    )}
                </div>
                <div style={{
                    backgroundColor: '#2d2d2d',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    padding: '20px'
                }}>
                    {/* Extraction Stats */}
                    {extractionStats && Object.keys(extractionStats).length > 0 && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px',
                            marginBottom: '20px'
                        }}>
                            {extractionStats.total_extracted !== undefined && (
                                <SummaryCard 
                                    label="Total Extracted" 
                                    value={formatValue(extractionStats.total_extracted)} 
                                />
                            )}
                            {extractionStats.after_deduplication !== undefined && (
                                <SummaryCard 
                                    label="After Deduplication" 
                                    value={formatValue(extractionStats.after_deduplication)} 
                                />
                            )}
                            {extractionStats.after_removing_nonsensical !== undefined && (
                                <SummaryCard 
                                    label="After Removing Nonsensical" 
                                    value={formatValue(extractionStats.after_removing_nonsensical)} 
                                />
                            )}
                            {extractionStats.final_count !== undefined && (
                                <SummaryCard 
                                    label="Final Count" 
                                    value={formatValue(extractionStats.final_count)} 
                                />
                            )}
                        </div>
                    )}

                    {/* Contacts List */}
                    {contactsCount > 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            {contactsList.map((contact, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleContactClick(contact)}
                                    style={{
                                        backgroundColor: '#181818',
                                        border: '1px solid rgba(100, 181, 246, 0.12)',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#1f1f1f';
                                        e.currentTarget.style.borderColor = 'rgba(100, 181, 246, 0.25)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#181818';
                                        e.currentTarget.style.borderColor = 'rgba(100, 181, 246, 0.12)';
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '16px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                color: '#e0e0e0',
                                                marginBottom: '8px'
                                            }}>
                                                {getContactName(contact)}
                                            </div>
                                            <div style={{
                                                fontSize: '13px',
                                                color: '#a0a0a0',
                                                marginBottom: '8px'
                                            }}>
                                                {getContactSummary(contact)}
                                            </div>
                                            {contact.source_url && (
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#909090',
                                                    marginTop: '8px'
                                                }}>
                                                    Source: <a 
                                                        href={contact.source_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ color: '#60a5fa', textDecoration: 'none' }}
                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                    >
                                                        {contact.source_url.length > 60 ? `${contact.source_url.substring(0, 60)}...` : contact.source_url}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: '12px',
                                            alignItems: 'center',
                                            flexWrap: 'wrap'
                                        }}>
                                            {contact.overall_score !== undefined && contact.overall_score !== null && (
                                                <div style={{
                                                    backgroundColor: '#0c0c0c',
                                                    border: `1px solid ${getScoreColor(contact.overall_score)}`,
                                                    borderRadius: '6px',
                                                    padding: '8px 12px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    minWidth: '60px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '11px',
                                                        color: '#909090',
                                                        textTransform: 'uppercase',
                                                        marginBottom: '4px',
                                                        fontWeight: '600',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        Score
                                                    </div>
                                                    <div style={{
                                                        fontSize: '18px',
                                                        color: getScoreColor(contact.overall_score),
                                                        fontWeight: '700'
                                                    }}>
                                                        {formatValue(contact.overall_score)}
                                                    </div>
                                                </div>
                                            )}
                                            {contact.is_nonsensical && (
                                                <div style={{
                                                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                                    borderRadius: '6px',
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    color: '#ef4444',
                                                    fontWeight: '600'
                                                }}>
                                                    Nonsensical
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#909090',
                            fontSize: '14px'
                        }}>
                            No contacts found
                        </div>
                    )}
                </div>
            </div>

            {/* Contact Detail Modal */}
            {selectedContact && (
                <ContactDetailModal
                    isOpen={isDetailModalOpen}
                    onClose={handleCloseDetailModal}
                    contact={selectedContact}
                />
            )}
        </>
    );
};

export default ContactsSection;

